import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, UserCircle2 } from "lucide-react";
import { User } from "@/types/common/user";
import { cn } from "@/lib/utils";
import { SectionHeader, FieldRow, FieldLabel, ErrorMsg } from "./FormPrimitives";

interface StaffSearchSectionProps {
  staffSearchQuery: string;
  staffResults: User[];
  selectedStaff: User | null;
  staffSearchLoading: boolean;
  onSearchChange: (value: string) => void;
  onStaffSelect: (userId: string) => void;
  onClearStaff: () => void;
  error?: string;
  disabled: boolean;
}

export function StaffSearchSection({
  staffSearchQuery,
  staffResults,
  selectedStaff,
  staffSearchLoading,
  onSearchChange,
  onStaffSelect,
  onClearStaff,
  error,
  disabled,
}: StaffSearchSectionProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <SectionHeader step={3} label="Staff Member" />
      <FieldRow>
        <FieldLabel htmlFor="staff-search" required>
          Staff Name
        </FieldLabel>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            id="staff-search"
            placeholder="Search by name…"
            value={staffSearchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={disabled}
            autoComplete="off"
            className={cn("pl-9", error && "border-destructive")}
          />
          {staffSearchLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>

        {staffResults.length > 0 && !selectedStaff && (
          <div className="mt-1 rounded-md border border-border bg-popover shadow-md overflow-hidden">
            {staffResults.map((s) => (
              <button
                key={s.Id}
                type="button"
                onClick={() => onStaffSelect(String(s.Id))}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-accent focus:bg-accent focus:outline-none border-b border-border last:border-b-0"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  {s.FullName?.charAt(0)?.toUpperCase() ?? "?"}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {s.FullName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {s.Role}
                    {s.City ? ` · ${s.City}` : ""}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {selectedStaff && (
          <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
            <UserCircle2 className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {selectedStaff.FullName}
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedStaff.Role}
                {selectedStaff.City ? ` · ${selectedStaff.City}` : ""}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground h-7"
              onClick={onClearStaff}
            >
              Change
            </Button>
          </div>
        )}

        <ErrorMsg msg={error} />
      </FieldRow>
    </div>
  );
}
